import React from 'react';
import { Link } from 'react-router-dom';
import ReactCountryFlag from 'react-country-flag';
import { calculateVoteWeight } from '../../utils/voteCalculations';
import useNow from '../../hooks/useNow';
import { turnAt, scheduleStatus } from '../../utils/schedule';
import { StatusBadge } from '../Producers/ProducersScheduleTable';
import ScheduleTicker from '../Producers/ScheduleTicker';

/**
 * "Top Block Producers" compacto de la portada (top N por votos).
 */
const TopProducers = ({ producers, active = [], lagMs = 0, chainFamily, limit = 10 }) => {
  const now = useNow(1000) - lagMs;
  const top = producers.slice(0, limit);
  const n = active.length;
  const cur = n ? turnAt(n, now).index : -1;
  const statusOf = (p, rank) => {
    if (!n) return null;
    const slot = active.indexOf(p.owner);
    if (slot >= 0 && slot === cur) return 'live';
    return scheduleStatus({ rank, inSchedule: slot >= 0, isTelos: chainFamily === 'TELOS' });
  };

  return (
    <section className="card min-w-0 flex flex-col">
      <div className="card-header">
        <h2 className="card-title m-0">Top Block Producers</h2>
        <span className="text-xs text-gray-500">
          {producers.length} active{n ? ` · ${n} in schedule` : ''}
        </span>
      </div>
      <ScheduleTicker active={active} lagMs={lagMs} />
      {top.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-gray-500">No active producers to display.</p>
      ) : (
        <div className="overflow-x-auto flex-1">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-2.5 pl-5 pr-2 font-medium">#</th>
                <th className="py-2.5 px-2 font-medium">Producer</th>
                <th className="py-2.5 px-2 font-medium hidden sm:table-cell">Country</th>
                <th className="py-2.5 px-2 font-medium text-right">Votes</th>
                <th className="py-2.5 pl-2 pr-5 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {top.map((p, i) => {
                const country = p.producerInfo?.country;
                return (
                  <tr key={p.owner} className="border-t border-gray-100">
                    <td className="py-2.5 pl-5 pr-2 text-gray-500">{i + 1}</td>
                    <td className="py-2.5 px-2"><Link to={`/account-detail/${p.owner}`} className="link">{p.owner}</Link></td>
                    <td className="py-2.5 px-2 text-gray-700 hidden sm:table-cell whitespace-nowrap">
                      {country ? (
                        <span className="inline-flex items-center gap-1.5">
                          <ReactCountryFlag countryCode={country} svg style={{ width: '1em', height: '1em' }} />
                          {country}
                        </span>
                      ) : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="py-2.5 px-2 text-right font-mono text-[13px] whitespace-nowrap">
                      {calculateVoteWeight(parseFloat(p.total_votes), chainFamily).toLocaleString('en-US', { maximumFractionDigits: 0 })}
                    </td>
                    <td className="py-2.5 pl-2 pr-5">
                      {statusOf(p, i + 1) && <StatusBadge status={statusOf(p, i + 1)} />}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <Link to="/producers" className="card-footer-link mt-auto">View all producers →</Link>
    </section>
  );
};

export default React.memo(TopProducers);
